import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  ViewChild
} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { AdService, AdSlotConfig } from '../../../core/services/ad.service';
import { CookieConsentService } from '../../../core/services/cookie-consent.service';
import { UserService } from '../../../core/services/user.service';

type AdPlacement = 'home' | 'convert' | 'playlists' | 'default';

const SLOT_CONFIGS: Record<AdPlacement, { desktop: AdSlotConfig; mobile: AdSlotConfig }> = {
  default: {
    desktop: { key: '9f7012ffbe0897ae646f08d5a02dbcb4', width: 728, height: 90 },
    mobile: { key: '3f294009553d2700a0b00fe79cc41e33', width: 300, height: 250 }
  },
  home: {
    desktop: { key: '9f7012ffbe0897ae646f08d5a02dbcb4', width: 728, height: 90 },
    mobile: { key: '3f294009553d2700a0b00fe79cc41e33', width: 300, height: 250 }
  },
  convert: {
    desktop: { key: '9f7012ffbe0897ae646f08d5a02dbcb4', width: 728, height: 90 },
    mobile: { key: '3f294009553d2700a0b00fe79cc41e33', width: 300, height: 250 }
  },
  playlists: {
    desktop: { key: '9f7012ffbe0897ae646f08d5a02dbcb4', width: 728, height: 90 },
    mobile: { key: '3f294009553d2700a0b00fe79cc41e33', width: 300, height: 250 }
  }
};

@Component({
  selector: 'app-ad-slot',
  templateUrl: './ad-slot.component.html',
  styleUrls: ['./ad-slot.component.scss']
})
export class AdSlotComponent implements AfterViewInit, OnDestroy {
  @Input() placement: AdPlacement = 'default';
  @ViewChild('adContainer', { static: false }) adContainer?: ElementRef<HTMLDivElement>;

  adReady = false;
  loading = false;
  adsConsent = false;
  isPremium = false;
  currentWidth = 728;
  currentHeight = 90;
  placeholderSrc?: string;
  placeholderAlt = 'Advertisement placeholder';

  private subscription?: Subscription;

  constructor(
    private adService: AdService,
    private cookieConsentService: CookieConsentService,
    private userService: UserService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.subscription = combineLatest([this.cookieConsentService.adsConsent$, this.userService.user$]).subscribe(
      async ([adsConsent, user]) => {
        this.isPremium = Boolean(user?.isPremium);
        this.adsConsent = adsConsent;

        if (this.isPremium) {
          this.teardownAd();
          this.placeholderSrc = undefined;
          this.cdr.markForCheck();
          return;
        }

        if (adsConsent) {
          this.ensurePlaceholderImage();
          await this.renderAd();
        } else {
          this.displayPlaceholder();
        }

        this.cdr.markForCheck();
      }
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  get showPlaceholder(): boolean {
    return !this.isPremium && (!this.adsConsent || !this.adReady);
  }

  private async renderAd(): Promise<void> {
    if (!this.adContainer) {
      return;
    }

    const config = this.resolveConfig();
    if (!config) {
      return;
    }

    this.currentWidth = config.width;
    this.currentHeight = config.height;
    this.loading = true;
    this.adReady = false;
    this.cdr.markForCheck();

    this.ensurePlaceholderImage();
    const loaded = await this.adService.loadAdSlot(this.adContainer.nativeElement, config);

    this.loading = false;
    this.adReady = loaded;

    if (!loaded) {
      this.placeholderSrc = this.adService.getRandomPlaceholder() ?? this.placeholderSrc;
      this.adService.clearSlot(this.adContainer.nativeElement);
    }
  }

  private resolveConfig(): AdSlotConfig | null {
    const baseConfig = SLOT_CONFIGS[this.placement] ?? SLOT_CONFIGS.default;
    const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    return isMobile ? baseConfig.mobile : baseConfig.desktop;
  }

  private teardownAd(): void {
    this.adReady = false;
    this.loading = false;
    this.adService.clearSlot(this.adContainer?.nativeElement);
  }

  private ensurePlaceholderImage(): void {
    if (!this.placeholderSrc) {
      this.placeholderSrc = this.adService.getRandomPlaceholder() ?? undefined;
    }
  }

  private displayPlaceholder(): void {
    this.placeholderSrc = this.adService.getRandomPlaceholder() ?? this.placeholderSrc;
    this.teardownAd();
  }
}
